import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter, take, timer, Subscription, Subject, takeUntil } from 'rxjs';
import { ModalService } from '@app/services/modal/modal.service';

interface TimeoutConfig {
  timeout?: number;          // total seconds
  timeoutAction?: string;    // where to navigate on full expiry
}

// Constants for timing configuration
const MODAL_POPUP_LEAD_TIME_SECONDS = 30000
const MODAL_POPUP_LEAD_TIME_MS = MODAL_POPUP_LEAD_TIME_SECONDS * 1000;
const MIN_TIMER_DELAY_MS = 1;

@Injectable({ providedIn: 'root' })
export class TimeoutService implements OnDestroy {
  private popupTimerSub?: Subscription;
  private finalActionTimerSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private modal: ModalService, 
    private zone: NgZone
  ) {

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$) 
    ).subscribe(() => {
        this.scheduleTimersBasedOnRoute(this.findDeepest(this.router.routerState.snapshot.root));
    });
  }

  private findDeepest(s: ActivatedRouteSnapshot | null): ActivatedRouteSnapshot | null {
    return s?.firstChild ? this.findDeepest(s.firstChild) : s;
  }


  public restartTimeoutTimer(): void {
    const currentRoute = this.findDeepest(this.router.routerState.snapshot.root);
    
    this.popupTimerSub?.unsubscribe();
    this.finalActionTimerSub?.unsubscribe();
    this.popupTimerSub = undefined;
    this.finalActionTimerSub = undefined;
    
    
    // Schedule new timers based on the current route
    this.scheduleTimersBasedOnRoute(currentRoute);
  }


  public isModalActive(): boolean {
      const isActive = this.modal.isTimeoutModalOpen();
      return isActive;
  }

  private scheduleTimersBasedOnRoute(snapshot: ActivatedRouteSnapshot | null): void {

    // --- 1. Cancel Previous Timers ---
    this.popupTimerSub?.unsubscribe();
    this.finalActionTimerSub?.unsubscribe();
    this.popupTimerSub = undefined;
    this.finalActionTimerSub = undefined;

    // --- 2. Check Snapshot and Timeout Configuration ---
    if (!snapshot) {
      return;
    }

    
    // Destructure timeout config from route data, providing a default for timeoutAction
    const { timeout, timeoutAction = '/splash' } = snapshot.data as TimeoutConfig;


    // Validate the timeout value
    if (!timeout || typeof timeout !== 'number' || timeout <= 0) {
      return;
    }
    // --- 3. Calculate Timer Durations ---
    const timeoutMs = timeout * 1000; // Total duration in ms
    let popupTimeMs: number; // Delay before showing popup modal (ms)
    let modalCountdownSeconds: number; // Starting countdown number for modal display (s)

    // Determine popup timing and modal countdown based on total timeout vs 30s threshold
    if (timeoutMs > MODAL_POPUP_LEAD_TIME_MS) {
      popupTimeMs = timeoutMs - MODAL_POPUP_LEAD_TIME_MS; // Popup 30s before end
      modalCountdownSeconds = MODAL_POPUP_LEAD_TIME_SECONDS; // Modal counts from 30
    } else {
      popupTimeMs = timeoutMs / 2; // Popup halfway
      modalCountdownSeconds = Math.ceil((timeoutMs - popupTimeMs) / 1000); // Modal counts remaining half (rounded up)
    }
    // Ensure minimum valid delays/values
    popupTimeMs = Math.max(MIN_TIMER_DELAY_MS, popupTimeMs);
    modalCountdownSeconds = Math.max(1, modalCountdownSeconds);

    // --- 4. Schedule New Timers ---
    this.zone.runOutsideAngular(() => {
      // Schedule Popup Timer
      this.popupTimerSub = timer(popupTimeMs).pipe(take(1)).subscribe(() => {
          this.zone.run(() => { // Re-enter zone to interact with ModalService
              this.modal.openModal(timeoutAction, 'timeout', modalCountdownSeconds);
          });
       });

      // Schedule Final Action Timer
      this.finalActionTimerSub = timer(timeoutMs).pipe(take(1)).subscribe(() => {
         this.zone.run(() => { 
           this.router.navigateByUrl(timeoutAction).finally(() => {
                window.location.reload();
            });
         });
       });
    });
  }

  /** Angular lifecycle hook for cleanup */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.popupTimerSub?.unsubscribe();
    this.finalActionTimerSub?.unsubscribe();
  }
}